// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-business-days';
import { Moment } from 'moment';
import { Button, Typography, Select, MenuItem, InputLabel, FormControl, FormHelperText } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Field, Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { CreateProjectThunk } from 'Features/project/projectThunk';
import { ReloadProjectsThunk, ResetFiltersThunk } from 'Features/projectList/projectListThunk';
import { resolveCreateProjectCtas, resolveDefaultProjectType } from 'Features/order/browseEntry';
import { getAllFoldersThunk } from 'Features/folderList/folderListThunk';
import { projectTypes } from 'Utils/constants';
import { TextField, Validators } from '../../forms';
import stylesDef from './styles';

interface CreateProjectFormProps {
  onCreate: (project: IProject) => void;
  onUpload: (project: IProject) => void;
  onCreateEstimate: (project: IProject) => void;
  onBrowseProducts: (project: IProject) => void;
}

interface CreateProjectValues {
  name: string;
  description?: string;
  referenceId?: string;
  type: string;
  folderId: string;
  anticipatedKickoffDate?: Moment | null;
  submitType?: string;
}

const NO_FOLDER = 'No folder';

// Spaces alone are not a name: the server trims, and an empty name cannot be stored.
const requiredName = (value?: string) => (value?.trim() ? undefined : 'Required');

// The kickoff date is optional; one that is given must be a whole date, today or later (the kickoff popup's words).
const validateKickoff = (value?: Moment | null) => {
  if (!value) return undefined;
  if (!value.isValid()) return 'Enter a date as MM/DD/YYYY.';
  if (value.isBefore(moment(), 'day')) return 'Pick today or a later date.';
  return undefined;
};

const CreateProjectForm = ({ onUpload, onCreate, onCreateEstimate, onBrowseProducts }: CreateProjectFormProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const { resources } = useAppSelector((state) => state.auth);
  const editorFunctions = !_ADMIN_ ? 'editorFunctions' in resources : true;
  const { utilitiesCatalog } = useFlags();
  const { folderList } = useAppSelector((state) => state.folderList);
  // Computed once at mount: a flag that resolves after the dialog opens must not reset a half-filled form.
  const [initialValues] = useState<Partial<CreateProjectValues>>(() => ({
    type: resolveDefaultProjectType({ isAdmin: _ADMIN_, utilitiesCatalog }),
    folderId: '',
  }));

  useEffect(() => {
    // Treasure Chest has no read of the customer's folders yet (AUT-6630 D3), so its Folder stays empty and disabled.
    if (!_ADMIN_) dispatch(getAllFoldersThunk());
  }, []);

  const folderName = (folderId: string) => folderList.find((folder) => folder._id === folderId)?.name ?? NO_FOLDER;

  const onFormSubmit = async (values: CreateProjectValues) => {
    const { submitType, anticipatedKickoffDate: kickoff, folderId, ...fields } = values;
    const anticipatedKickoffDate = kickoff ? kickoff.format('YYYY-MM-DD') : undefined;
    let result: IApiResponse<IProject>;
    try {
      // A project goes into a folder only when one is picked; "No folder" sends none.
      result = await dispatch(CreateProjectThunk({ ...fields, ...(folderId ? { folderId } : {}), anticipatedKickoffDate }, editorFunctions));
    } catch (e) {
      return { [FORM_ERROR]: 'Failed to create project' };
    }
    // A rejected name (already taken by a folder or project) is reported under the Project Name
    // field; any other failure is reported for the form as a whole. Nothing was created either way.
    if (!result.success) {
      return result.errors?.name ? { name: result.errors.name } : { [FORM_ERROR]: 'Failed to create project' };
    }
    if (!_ADMIN_) {
      dispatch(ResetFiltersThunk());
      dispatch(ReloadProjectsThunk());
    }

    if (result.success) {
      if (submitType === 'processProject') {
        window?.pendo?.track('Process A Project', { projectId: result.data?._id });
        if (_ADMIN_) onCreate(result.data);
        onUpload(result.data);
      } else if (submitType === 'browseProducts') {
        window?.pendo?.track('Browse Products', { projectId: result.data?._id });
        onBrowseProducts(result.data);
      } else {
        window?.pendo?.track('Create Estimate', {
          ...(kickoff && { kickoff_date: kickoff.format('MM-DD-YYYY') }),
          projectId: result.data?._id,
        });
        onCreateEstimate(result.data);
      }
    }
    return undefined;
  };

  return (
    <>
      <Box sx={styles.pageTitle}>
        <Typography variant="h1">Start New Project</Typography>
      </Box>
      <Form<CreateProjectValues>
        onSubmit={onFormSubmit}
        initialValues={initialValues}
        render={({ form, handleSubmit, submitError, modifiedSinceLastSubmit, values }) => {
          // Browse-first replaces the AOI-first estimate entry for flag-on Utilities projects.
          const { showBrowseProducts, showCreateEstimate } = resolveCreateProjectCtas({ isAdmin: _ADMIN_, projectType: values.type, utilitiesCatalog });
          return (
            // noValidate: the browser's own required-field bubble would block the submit before every field's
            // message shows. Each field validates itself, and one submit shows every problem under its own field.
            <form
              style={styles.form}
              onSubmit={handleSubmit}
              noValidate
            >
              <TextField
                name="name"
                label="Project Name"
                variant="outlined"
                placeholder="Enter Project Name"
                required
                validate={requiredName}
                sx={styles.textFiled}
              />
              <Box sx={styles.fields}>
                <Field name="type" validate={Validators.required}>
                  {({ input, meta }) => {
                    const typeError = meta.touched && meta.error;
                    return (
                      <FormControl sx={styles.projectType} required error={!!typeError}>
                        <InputLabel id="project-type-label">Project Type</InputLabel>
                        <Select
                          name={input.name}
                          value={input.value}
                          label="Project Type"
                          labelId="project-type-label"
                          onChange={input.onChange}
                          sx={{ ...styles.textFiled, ...styles.capitalize }}
                        >
                          {projectTypes.filter((item) => item !== 'custom').map((type) => <MenuItem key={type} value={type} sx={styles.capitalize}>{type === 'standard' ? 'AEC' : type}</MenuItem>)}
                        </Select>
                        {typeError && <FormHelperText>{meta.error}</FormHelperText>}
                      </FormControl>
                    );
                  }}
                </Field>
                {/* Folder is optional and never shows an error: an empty Folder reads "No folder", and "No folder"
                    is the first option, so a pick can be undone. */}
                <Field name="folderId">
                  {({ input }) => (
                    <FormControl sx={styles.projectType}>
                      <InputLabel id="folder-label" shrink>Folder</InputLabel>
                      <Select
                        name={input.name}
                        value={input.value}
                        label="Folder"
                        labelId="folder-label"
                        onChange={input.onChange}
                        displayEmpty
                        notched
                        renderValue={folderName}
                        sx={{ ...styles.textFiled, ...(input.value ? styles.capitalize : {}) }}
                        disabled={_ADMIN_ || folderList.length === 0}
                      >
                        <MenuItem value="">{NO_FOLDER}</MenuItem>
                        {folderList.map((folder) => <MenuItem key={folder._id} value={folder._id} sx={styles.capitalize}>{folder.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  )}
                </Field>
              </Box>
              <TextField
                name="description"
                label="Description"
                placeholder="Description"
                variant="outlined"
                sx={styles.textFiled}
              />
              <Box sx={styles.fields}>
                <TextField
                  name="referenceId"
                  label="Reference ID"
                  placeholder="Reference ID"
                  variant="outlined"
                  sx={styles.textFiled}
                />
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <Field name="anticipatedKickoffDate" validate={validateKickoff}>
                    {({ input, meta }) => {
                      const kickoffError = meta.touched && meta.error ? meta.error : undefined;
                      return (
                        <DatePicker
                          format="MM/DD/YYYY"
                          value={input.value || null}
                          onChange={input.onChange}
                          sx={styles.datePicker}
                          label="Anticipated Kickoff"
                          disablePast
                          slotProps={{ textField: { error: !!kickoffError, helperText: kickoffError, onBlur: input.onBlur } }}
                        />
                      );
                    }}
                  </Field>
                </LocalizationProvider>
              </Box>
              {submitError && !modifiedSinceLastSubmit && (
                <FormHelperText error sx={styles.formError}>{submitError}</FormHelperText>
              )}
              <Box sx={styles.buttonsWrapper}>
                <Button
                  variant="outlined"
                  sx={styles.uploadFormSubmit}
                  type="submit"
                  onClick={() => { form.change('submitType', 'processProject'); }}
                >
                  PROCESS A PROJECT
                </Button>
                {showBrowseProducts && (
                  <Button
                    variant="contained"
                    color="primaryAction"
                    sx={styles.browseProductsSubmit}
                    type="submit"
                    onClick={() => { form.change('submitType', 'browseProducts'); }}
                  >
                    BROWSE PRODUCTS
                  </Button>
                )}
                {showCreateEstimate && (
                  <Button
                    variant="outlined"
                    sx={styles.uploadFormSubmit}
                    type="submit"
                    onClick={() => { form.change('submitType', 'createEstimate'); }}
                  >
                    CREATE ESTIMATE
                  </Button>
                )}
              </Box>
            </form>
          );
        }}
      />
    </>
  );
};

CreateProjectForm.propTypes = {
  onUpload: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onCreateEstimate: PropTypes.func.isRequired,
  onBrowseProducts: PropTypes.func.isRequired,
};

export default CreateProjectForm;
